import { Fragment } from 'react';
import {
  Callout,
  Chip,
  Facts,
  Field,
  Panel,
  Select,
  TableScroll,
  countryOptions,
  explainFailure,
} from '@affdice/portal-kit';
import { PlatformApi } from '../../../client.ts';
import { t } from '../../../messages.ts';
import { goLiveGate, lifecycleLabel, lifecycleMeaning } from '../../../screens.ts';

/**
 * Go-live — the operator's state, its country and counts, and the conditions
 * still outstanding before it may go live. One panel: everything here is one
 * answer to one question.
 */
type OperatorDetailData = Awaited<ReturnType<PlatformApi['operatorDetail']>>;

export function GoLiveSection({
  api,
  operatorId,
  canWrite,
  detail,
  country,
  refresh,
  onChanged,
}: {
  api: PlatformApi;
  operatorId: string;
  canWrite: boolean;
  detail: OperatorDetailData;
  /**
   * The country edit's draft and its failure, held by the detail popup so
   * leaving this section and coming back keeps what was being typed.
   */
  country: {
    countryEdit: string | null;
    setCountryEdit: (v: string | null) => void;
    countryError: unknown;
    setCountryError: (e: unknown) => void;
  };
  refresh: () => void;
  onChanged?: (() => void) | undefined;
}) {
  const { countryEdit, setCountryEdit, countryError, setCountryError } = country;
  const gate = goLiveGate(detail.conditions);
  const state = String(detail.operator['state'] ?? 'PROSPECT');
  // Read once so the go-live panel can put Country in front of them while it
  // is being read, and beside the edit form while it is being changed,
  // without the list being written out twice.
  const countFacts = Object.entries(detail.counts).map(([k, v]) => ({
    label: k.replace(/_/g, ' '),
    value: v,
  }));
  return (
    /*
       One panel, not seven fragments.
       Everything here — the state, the country it operates from, its
       counts and its outstanding conditions — is one answer to one
       question, and it was rendered as seven siblings of the split's own
       main column. That column spaces its children a section apart, so
       each fragment read as a thing of its own: a lone button under a
       callout, a lone field under that, a grid of cards adrift beneath
       it. Inside a panel they share one rhythm and one border, and the
       one action the panel offers sits in its header where every other
       panel in this console puts its own.
    */
    /* Suspend/reactivate moved to Profile, beside delete: both change
       whether this operator runs at all, and neither is a go-live
       condition. */
    <Panel title={t('ops.goLive.title')}>
      {/* Item 6 (third batch) — the state's own label, said once. This
          paired the raw constant with a sentence that also opened with
          the state name, so LIVE read "LIVE. Live. Events are billed…". */}
      <Callout tone={state === 'LIVE' ? 'note' : 'caution'}>
        <strong>{lifecycleLabel(state)}.</strong>{' '}{lifecycleMeaning(state)}
      </Callout>

      {/*
        Country is a fact about this operator, so it is read in the same
        grid as its other facts rather than as a form standing on its
        own. A `form-row` gave it a two-line label block, a control-width
        column and the rest of the pane empty beside it: a label, then a
        bare em dash and an Edit adrift on the line under it.

        Item 3 (third batch) — country did not exist as a fact until now,
        so an operator created before it reads "—" until a superadmin
        sets it. Item 4 (third batch) — the Edit is hidden from a
        read-only role; platform.admin is still what the server enforces,
        and that check has not moved.

        Editing is the one state that is genuinely a form, and it keeps
        the form row: a Select, its Save and its Cancel line up there the
        way they do on every other edit in this console.
      */}
      {countryEdit === null ? (
        <Facts
          items={[
            {
              label: t('common.country'),
              value: (
                <span className="row">
                  {String(detail.operator['country'] ?? '') || '—'}
                  {canWrite ? (
                    <button
                      type="button"
                      className="link-button"
                      onClick={() => { setCountryError(null); setCountryEdit(''); }}
                    >
                      {t('common.edit')}
                    </button>
                  ) : null}
                </span>
              ),
            },
            ...countFacts,
          ]}
        />
      ) : (
        <>
          {countryError ? <Callout tone="error">{explainFailure(countryError).message}</Callout> : null}
          <div className="form-row">
            <Field label={t('common.country')}>
              <Select
                value={countryEdit}
                onChange={setCountryEdit}
                options={[{ value: '', label: t('ops.goLive.choose') }, ...countryOptions()]}
              />
            </Field>
            <button
              type="button"
              className="copy"
              disabled={countryEdit.trim().length !== 2}
              onClick={() => {
                setCountryError(null);
                void api.setOperatorCountry(operatorId, countryEdit.trim())
                  .then(() => { setCountryEdit(null); refresh(); onChanged?.(); })
                  .catch((e: unknown) => setCountryError(e));
              }}
            >
              {t('common.save')}
            </button>
            <button type="button" className="link-button" onClick={() => { setCountryError(null); setCountryEdit(null); }}>
              {t('common.cancel')}
            </button>
          </div>
          <Facts items={countFacts} />
        </>
      )}

      <Callout tone={gate.canGoLive ? 'note' : 'caution'}>
        {gate.verdict}
      </Callout>

      <TableScroll>
        <table>
          <thead>
            <tr><th className="num">#</th><th>{t('ops.goLive.condition')}</th><th>{t('common.state')}</th></tr>
          </thead>
          <tbody>
            {/* Batch 2 item 13 — the note (or waiver reason) used to
                stretch this cell to several lines and, at 390px, push
                State off screen. It moves to a row note below,
                spanning the three columns above (#, Condition, State). */}
            {gate.lines.map((c) => {
              const note = c.note ?? c.waivedBecause ?? '';
              return (
                <Fragment key={c.no}>
                  <tr className={note ? 'has-note' : ''}>
                    <td className="num faint">{c.no}</td>
                    <td className="wrap">{c.what}</td>
                    <td>
                      {/* Batch 2 item 6 — outstanding is a go-live gap
                          to look at, not money that can still change. */}
                      {c.met ? <Chip tone="locked">{t('ops.goLive.pass')}</Chip> : <Chip tone="attention">{t('ops.goLive.outstanding')}</Chip>}
                    </td>
                  </tr>
                  {note ? (
                    <tr className="row-note">
                      <td colSpan={3}><span className="row-note-text">{note}</span></td>
                    </tr>
                  ) : null}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </TableScroll>

      <Callout>{detail.note}</Callout>
    </Panel>
  );
}
